import { parseOrThrow } from "_shared/_utilities/zod-schema-parser";
import { z } from "zod";

/** What happened to a logged email, as the API names it. */
export const EmailLogOutcomeSchema = z.enum(["Sent", "Captured", "Failed"]);
export type EmailLogOutcome = z.infer<typeof EmailLogOutcomeSchema>;

const summary = z.object({
  id: z.uuid(),
  recordedAt: z.string(),
  environment: z.string(),
  template: z.string().nullish(),
  recipient: z.string().nullish(),
  carbonCopy: z.string(),
  subject: z.string().nullish(),
  outcome: EmailLogOutcomeSchema,
});

const detail = summary.extend({
  sender: z.string().nullish(),
  blindCarbonCopy: z.string(),
  htmlBody: z.string().nullish(),
  textBody: z.string().nullish(),
  linksMasked: z.boolean(),
  bodiesRemovedAt: z.string().nullish(),
  providerMessageId: z.string().nullish(),
  error: z.string().nullish(),
  correlationKind: z.string().nullish(),
  correlationId: z.uuid().nullish(),
});

export type EmailLogEntrySummaryJSON = z.infer<typeof summary>;
export type EmailLogEntryJSON = z.infer<typeof detail>;

/** The email log's two shapes on the wire: a table row, and one entry in full (#6922). */
export default class EmailLogEntrySchema {
  public static readonly summary = summary;
  public static readonly detail = detail;

  public static validateSummary(values: unknown): EmailLogEntrySummaryJSON {
    return parseOrThrow(EmailLogEntrySchema.summary, values, {
      logError: true,
      schemaName: "EmailLogEntrySchema.summary",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static validate(values: unknown): EmailLogEntryJSON {
    return parseOrThrow(EmailLogEntrySchema.detail, values, {
      logError: true,
      schemaName: "EmailLogEntrySchema.detail",
      operation: "validate",
      payloadSample: values,
    });
  }

  public static isValid(values: unknown): values is EmailLogEntryJSON {
    return EmailLogEntrySchema.detail.safeParse(values).success;
  }
}
